import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import {
  CookieDisclaimer,
  getCookieConsent,
  useCookieConsent,
  type CookieChoice,
} from "@libertify/ui";
import { useSession } from "@/hooks/useSession";
import { useConsentsApi } from "@/hooks/useConsentsApi";

type CookieConsentContextValue = {
  /** Reopen the cookie banner (e.g. from the profile menu) */
  openCookieSettings: () => void;
};

const CookieConsentContext = createContext<CookieConsentContextValue | null>(
  null
);

export type CookieConsentProviderProps = {
  children: ReactNode;
};

// One hook call for the whole app, so the banner and the menu share its open state
export const CookieConsentProvider = ({
  children,
}: CookieConsentProviderProps) => {
  // Signed-in users only, so the banner never shows on login/signup pages
  const { isAuthenticated, user } = useSession();
  // Without a local answer, check the server before asking again.
  // useCookieConsent below re-renders on every answer, so this read stays current.
  const { isRestoring, recordAnalyticsConsent } = useConsentsApi({
    userId: user?.id,
    enabled: isAuthenticated && getCookieConsent() === null,
  });
  const recordChoice = useCallback(
    ({ decision, method }: CookieChoice) =>
      recordAnalyticsConsent(decision, method),
    [recordAnalyticsConsent]
  );
  const { isOpen, accept, reject, openSettings } = useCookieConsent({
    onChoice: recordChoice,
  });

  const value = useMemo(
    () => ({ openCookieSettings: openSettings }),
    [openSettings]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
      <CookieDisclaimer
        isOpen={isAuthenticated && !isRestoring && isOpen}
        onAccept={accept}
        onReject={reject}
      />
    </CookieConsentContext.Provider>
  );
};

export const useCookieSettings = (): CookieConsentContextValue => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error(
      "useCookieSettings must be used within a CookieConsentProvider"
    );
  }
  return context;
};
